import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';

// PrimeNG modules
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

// Services and models
import { CommonService, ToastMessageService } from '@fitness-force/api';
import { ProductService } from '@fitness-force/retail-billing';

import { ProductResponsePagedResults, ProductResponse } from '@fitness-force/retail-billing';
import { COMPANY_INFO, TENANTS_INFO } from 'apps/ff-frontend/src/app/state/app.state';
import { InputNumberComponent } from '../input-number/input-number.component';

export interface PlanProduct {
  sku_id: number;
  product_sku?: ProductResponse;
  quantity: number;
  amount: number;
  product_name?: string;
}

@Component({
  selector: 'fitness-force-plan-product-modal',
  templateUrl: './plan-product-modal.component.html',
  styleUrls: ['./plan-product-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    DividerModule,
    InputTextModule,
    InputNumberModule,
    InputNumberComponent,
    SelectModule,
    SkeletonModule,
    TooltipModule
  ]
})
export class PlanProductModalComponent implements OnInit {
  getAccessTenantList$: Observable<any> = new Observable();
  tenantId: number = 0;
  
  activeEditProduct!: any;
  activeEditIndex!: number;
  productCreateForm!: UntypedFormGroup;
  products: ProductResponse[] = [];
  currencySymbol: any;
  subscriptionCategory!: any;
  planType!: any;
  planId: number = 0;
  isSecondaryLanguage = false;
  primaryLanguage!: string;
  secondaryLanguage!: string;
  mode: string = 'plans';
  type: string = 'single';
  
  loading = new BehaviorSubject<boolean>(false);

  constructor(
    private store: Store,
    private productService: ProductService,
    private toastMessageService: ToastMessageService,
    private translate: TranslateService,
    private ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private commonService: CommonService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    if (this.config.data) {
      this.activeEditProduct = this.config.data.product;
      this.activeEditIndex = this.config.data.index;
      this.subscriptionCategory = this.config.data.subscriptionCategory;
      this.planType = this.config.data.planType;
      this.planId = this.config.data.planId;
      this.mode = this.config.data.mode || 'plans';
      this.type = this.config.data.type || 'single';
    }
    this.buildForm();
  }

  ngOnInit(): void {
    this.componentInit();
  }

  componentInit() {
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    combineLatest([
      this.getAccessTenantList$,
      this.store.select(COMPANY_INFO),
    ]).subscribe({
      next: ([tenant, company]: any) => {
        this.tenantId = tenant.selected_tenant.id;
        this.currencySymbol = company.currency_symbol;
        
        if (tenant.selected_tenant?.secondary_language) {
          this.isSecondaryLanguage = true;
          this.translate
            .get([
              'common.LANGUAGE.' +
              tenant.selected_tenant.primary_language?.toUpperCase(),
              'common.LANGUAGE.' +
              tenant.selected_tenant.secondary_language?.toUpperCase(),
            ])
            .subscribe((translations) => {
              this.primaryLanguage =
                translations[
                'common.LANGUAGE.' +
                tenant.selected_tenant.primary_language?.toUpperCase()
                ];
              this.secondaryLanguage =
                translations[
                'common.LANGUAGE.' +
                tenant.selected_tenant.secondary_language?.toUpperCase()
                ];
            });
        }
        
        if (this.tenantId > 0) {
          this.preFormData();
        }
      },
    });
  }

  preFormData() {
    this.loading.next(true);
    const requestParams = new Map();
    // Add pagination params if needed
    requestParams.set('PageSize', { name: 'PageSize', value: 1000 });
    requestParams.set('PageNumber', { name: 'PageNumber', value: 1 });
    
    this.productService.getTenantProducts(this.tenantId, requestParams).subscribe({
      next: (response: ProductResponsePagedResults) => {
        // Filter active products
        this.products = response.items?.filter(item => item.is_active) || [];
        this.loading.next(false);
        this.cd.detectChanges();
      },
      error: (error: any) => {
        this.loading.next(false);
        this.handleError(error);
      },
    });
  }

  handleError(_error: any) {
    this.translate
      .get([
        'common.errors.SERVER_SIDE.ERROR_OCCURRED',
        'common.errors.SERVER_SIDE.SOMETHING_WRONG',
      ])
      .subscribe((translations: any) => {
        this.toastMessageService.showErrorMessage(
          translations['common.errors.SERVER_SIDE.SOMETHING_WRONG']
        );
      });
  }

  buildForm() {
    this.productCreateForm = this.fb.group({
      product_id: [
        this.activeEditProduct && this.activeEditProduct.id
          ? this.activeEditProduct.id
          : null,
        [Validators.required],
      ],
      amount: [
        this.activeEditProduct && this.activeEditProduct.amount_in_minor_currency
          ? this.commonService.ConvertAmountFromMinorCurrency(this.activeEditProduct.amount_in_minor_currency)
          : 0,
        [Validators.required, Validators.min(0)],
      ]
    });

  }

  saveProductCreateForm() {
    if (this.productCreateForm.valid) {
      const values = this.productCreateForm.getRawValue();
      const cleanedValues = this.cleanProductFormValues(values);
      
      // Find the selected product to get additional details
      const selectedProduct = this.products.find(p => p.id === cleanedValues.product_id);
      const planproduct = {
        id: cleanedValues.product_id,
        product_id: cleanedValues.product_id,
        plan_id: this.planId,
        amount_in_minor_currency: this.commonService.ConvertMinorCurrencyFromAmount(cleanedValues.amount),
        product_name: selectedProduct?.name_primary_language,
        product_sku: ""
      }
      cleanedValues.amount = this.commonService.ConvertMinorCurrencyFromAmount(cleanedValues.amount);

      if(this.activeEditProduct){
        this.activeEditProduct.amount_in_minor_currency = cleanedValues.amount;
        this.activeEditProduct.product_id = cleanedValues.product_id;
        this.activeEditProduct.product_name = selectedProduct?.name_primary_language;
        this.activeEditProduct.product_sku = "";
        this.ref.close({ 
          product: this.activeEditProduct, 
          index: this.activeEditIndex 
        });
      }
      else{
        this.ref.close({ 
          product: planproduct, 
          index:  this.activeEditIndex 
        });
      }

      

     
    } else {
      Object.keys(this.productCreateForm.controls).forEach((field) => {
        const control = this.productCreateForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }

  cleanProductFormValues(values: any) {
    const cleanedValues = { ...values };
    return cleanedValues;
  }

  closeDialog() {
    this.ref.close();
  }

  // Form getters
  get ProductSku(): AbstractControl | null {
    return this.productCreateForm.get('sku_id');
  }

  get Amount(): AbstractControl | null {
    return this.productCreateForm.get('amount');
  }
}